import {
  FOCUSABLE_ELEMENTS,
  KLAVIYO_BIS_ENDPOINT,
  KLAVIYO_COMPANY_ID,
  KLAVIYO_LIST_ID,
  SUBSCRIBE_SERVICE,
} from '../config/constants';
import addClass from '../utils/dom/addClass';
import click from '../utils/dom/click';
import removeClass from '../utils/dom/removeClass';
import { lockScroll, unlockScroll } from '../utils/dom/scroll';
import { KEY_CODES, KEY_DOWN } from '../utils/keys';

const ON = 'on';
let currentTab = 1;
let focusableContent = null;
// let variant = window.VARIANT;
// let interval = null;

const setupInquire = (variant) => {
  const inquireButton = document.querySelector('button.inquire-button');
  const comingSoonButton = document.querySelector('.coming-soon-button');
  const addToBagButton = document.querySelector('button.add-to-bag-button');

  if (!inquireButton && !addToBagButton) {
    return;
  }

  const bg = document.getElementById('inquire-overlay-background');
  const inquireOverlay = document.getElementById('inquire-overlay');
  const closeInquireOverlay = inquireOverlay?.querySelector('.hmr-overlay-close');
  const notifyOverlay = document.getElementById('notify-overlay');
  const closeNotifyOverlay = notifyOverlay?.querySelector('.hmr-overlay-close');
  const bisOverlay = document.getElementById('bis-overlay');
  const closeBisOverlay = bisOverlay?.querySelector('.hmr-overlay-close');

  // Only append if not already in the DOM
  if (inquireOverlay && inquireOverlay.parentNode !== document.body) {
    document.body.appendChild(inquireOverlay);
  }
  if (bisOverlay && bisOverlay.parentNode !== document.body) {
    document.body.appendChild(bisOverlay);
  }

  const onKeyDown = (event) => {
    const isTabPressed = event.key === KEY_CODES.TAB;

    if (event.key === KEY_CODES.ESC) {
      closeOverlays();
      inquireButton.focus();
      return;
    }

    if (!isTabPressed) {
      return;
    }

    if (event.shiftKey) {
      currentTab = currentTab - 1 < 0 ? focusableContent.length - 1 : currentTab - 1;
    } else {
      currentTab = currentTab === focusableContent.length - 1 ? 0 : currentTab + 1;
    }

    event.preventDefault();
    focusableContent[currentTab].focus();
  };

  const onNotifySubmit = async (event) => {
    event.preventDefault();
    const button = notifyOverlay.querySelector('button[type="submit"]');
    const innerHTML = button.innerHTML;

    const firstName = document.getElementById('notify-first-name');
    const lastName = document.getElementById('notify-last-name');
    const email = document.getElementById('notify-email');
    const phone = document.getElementById('notify-phone');

    button.setAttribute('disabled', 'disabled');
    button.innerHTML = 'Submitting...';

    const data = {
      email: email.value,
      firstName: firstName.value,
      lastName: lastName.value,
      phone: phone.value,
      consent: true,
    };
    const request = await fetch(`${SUBSCRIBE_SERVICE}subscribe`, {
      method: 'POST',
      body: JSON.stringify(data),
      headers: {
        'Content-Type': 'application/json',
      },
    });
    const response = await request.json();

    if (response.ok) {
      const success = notifyOverlay.querySelector('.hmr-overlay .field-success');
      addClass(success, ON);
      success.focus();
    } else {
      alert('There was an error subscribing to the waitlist. Please get in touch.');
    }
    button.removeAttribute('disabled');
    button.innerHTML = innerHTML;
  };

  const onBisSubmit = async (event) => {
    event.preventDefault();
    const button = bisOverlay.querySelector('button[type="submit"]');
    const innerHTML = button.innerHTML;

    const email = document.getElementById('bis-email');
    const phone = document.getElementById('bis-phone');
    const variantId = document.getElementById('bis-variant-id');

    button.setAttribute('disabled', 'disabled');
    button.innerHTML = 'Submitting...';

    // Klaviyo Back in Stock API format (2024-06-15)
    const profileAttributes = {
      email: email.value,
    };

    const channels = ['EMAIL'];

    // Add phone number if provided
    if (phone.value && phone.value.trim()) {
      // Format phone number to international format
      let phoneNumber = phone.value.trim();

      // Remove all non-digit characters except +
      phoneNumber = phoneNumber.replace(/[^\d+]/g, '');

      // If it doesn't start with +, assume it's a US number and add +1
      if (!phoneNumber.startsWith('+')) {
        // Remove any leading 1 if present
        if (phoneNumber.startsWith('1')) {
          phoneNumber = phoneNumber.substring(1);
        }
        // Add +1 prefix for US numbers
        phoneNumber = `+1${phoneNumber}`;
      }

      // Validate the format (should be + followed by country code and number)
      if (phoneNumber.length >= 10 && phoneNumber.startsWith('+')) {
        profileAttributes.phone_number = phoneNumber;
        channels.push('SMS');
      }
    }

    const payload = {
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          profile: {
            data: {
              type: 'profile',
              attributes: profileAttributes,
            },
          },
          channels: channels,
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variantId.value}`,
            },
          },
        },
      },
    };

    const requestOptions = {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-06-15',
      },
      body: JSON.stringify(payload),
    };

    try {
      const request = await fetch(`${KLAVIYO_BIS_ENDPOINT}?company_id=${KLAVIYO_COMPANY_ID}`, requestOptions);

      if (request.ok) {
        // If SMS is included, we need to also subscribe to a list for SMS consent
        if (channels.includes('SMS')) {
          const smsConsentPayload = {
            data: {
              type: 'subscription',
              attributes: {
                profile: {
                  data: {
                    type: 'profile',
                    attributes: profileAttributes,
                  },
                },
                custom_source: 'Back in Stock',
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: KLAVIYO_LIST_ID,
                  },
                },
              },
            },
          };

          const smsRequestOptions = {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              revision: '2024-06-15',
            },
            body: JSON.stringify(smsConsentPayload),
          };

          try {
            await fetch(`${KLAVIYO_BIS_ENDPOINT}?company_id=${KLAVIYO_COMPANY_ID}`, smsRequestOptions);
          } catch (smsError) {
            console.warn('SMS consent subscription failed:', smsError);
            // Don't fail the entire request if SMS consent fails
          }
        }

        const success = bisOverlay.querySelector('.field-success');
        addClass(success, ON);
        success.focus();
      } else {
        const errorData = await request.json();
        console.error('Klaviyo BIS API error:', errorData);
        alert('There was an error signing up for back in stock notifications. Please get in touch.');
      }
    } catch (error) {
      console.error('Error submitting to Klaviyo:', error);
      alert('There was an error signing up for back in stock notifications. Please get in touch.');
    }

    button.removeAttribute('disabled');
    button.innerHTML = innerHTML;
  };

  const closeOverlays = () => {
    if (inquireOverlay) {
      const elements = [...inquireOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
      elements.forEach((element) => {
        element.setAttribute('tabindex', -1);
      });
      closeInquireOverlay?.removeEventListener(click(), closeOverlays);
    }

    if (notifyOverlay) {
      const notifyElements = [...notifyOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
      notifyElements.forEach((element) => {
        element.setAttribute('tabindex', -1);
      });
      notifyOverlay.querySelector('form')?.removeEventListener('submit', onNotifySubmit);
      closeNotifyOverlay?.removeEventListener(click(), closeOverlays);
    }

    if (bisOverlay) {
      const bisElements = [...bisOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
      bisElements.forEach((element) => {
        element.setAttribute('tabindex', -1);
      });
      bisOverlay.querySelector('form')?.removeEventListener('submit', onBisSubmit);
      closeBisOverlay?.removeEventListener(click(), closeOverlays);
    }

    window.removeEventListener(KEY_DOWN, onKeyDown);
    bg?.removeEventListener(click(), closeOverlays);
    removeClass(bg, ON);
    removeClass(inquireOverlay, ON);
    removeClass(notifyOverlay, ON);
    removeClass(bisOverlay, ON);
    unlockScroll();
  };

  const openInquire = () => {
    lockScroll();

    if (variant) {
      const preview = document.getElementById(`inquire-preview-${variant.id}`);
      removeClass(inquireOverlay.querySelector('.inquire-preview.on'), ON);
      addClass(preview, ON);
    }

    // Reset success message
    const success = inquireOverlay.querySelector('.field-success');
    removeClass(success, ON);

    addClass(bg, ON);
    addClass(inquireOverlay, ON);

    const elements = [...inquireOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
    elements.forEach((element) => {
      element.setAttribute('tabindex', 0);
    });

    currentTab = 1;
    focusableContent = elements;
    focusableContent[0].focus();

    const body = document.getElementById('inquiry-body');
    const details = [
      ...inquireOverlay.querySelectorAll(
        '.inquire-preview.on .inquire-preview-details strong, .inquire-preview.on .inquire-preview-details div div',
      ),
    ];

    let text = 'Product inquiry: ';
    details.forEach((detail) => {
      text += detail.innerText + ' ';
    });
    body.innerText = text;

    closeInquireOverlay.addEventListener(click(), closeOverlays);
  };

  const openNotify = () => {
    lockScroll();

    if (variant) {
      // const preview = document.getElementById(`notify-preview-${variant.id}`);
      removeClass(notifyOverlay.querySelector('.inquire-preview.on'), ON);
      // addClass(preview, ON);
    }
    const success = notifyOverlay.querySelector('.hmr-overlay .field-success');
    removeClass(success, ON);

    addClass(bg, ON);
    addClass(notifyOverlay, ON);

    const elements = [...notifyOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
    elements.forEach((element) => {
      element.setAttribute('tabindex', 0);
    });

    currentTab = 1;
    focusableContent = elements;
    focusableContent[0].focus();

    notifyOverlay.querySelector('form').addEventListener('submit', onNotifySubmit);
    closeNotifyOverlay.addEventListener(click(), closeOverlays);
  };

  const openBis = () => {
    lockScroll();

    if (variant) {
      const preview = document.getElementById(`bis-preview-${variant.id}`);
      if (preview) {
        removeClass(bisOverlay.querySelector('.inquire-preview.on'), ON);
        addClass(preview, ON);
      }

      // Update the hidden fields with current variant information
      const variantIdField = document.getElementById('bis-variant-id');
      if (variantIdField) variantIdField.value = variant.id;
    }
    const success = bisOverlay.querySelector('.field-success');
    removeClass(success, ON);

    addClass(bg, ON);
    addClass(bisOverlay, ON);

    const elements = [...bisOverlay.querySelectorAll(FOCUSABLE_ELEMENTS)];
    elements.forEach((element) => {
      element.setAttribute('tabindex', 0);
    });

    currentTab = 1;
    focusableContent = elements;
    focusableContent[0].focus();

    bisOverlay.querySelector('form').addEventListener('submit', onBisSubmit);
    closeBisOverlay.addEventListener(click(), closeOverlays);
  };

  const openOverlay = (event) => {
    // Find the button element (in case event.target is a child element)
    const button = event.target.closest('button');

    if (button?.dataset.comingsoon === 'true') {
      openNotify();
    } else if (button?.dataset.bis === 'true') {
      openBis();
    } else if (button?.classList.contains('inquire-button')) {
      openInquire();
    }
    // If it's an add-to-bag button without data-bis, don't do anything
    // (let the addToCart function handle it)

    bg.addEventListener(click(), closeOverlays);
    window.addEventListener(KEY_DOWN, onKeyDown);
  };

  inquireButton?.addEventListener(click(), openOverlay);
  comingSoonButton?.addEventListener(click(), openOverlay);
  addToBagButton?.addEventListener(click(), openOverlay);

  if (window.location.hash === '#inquire-form') {
    const success = inquireOverlay.querySelector('.hmr-overlay .field-success');
    addClass(success, ON);
    inquireButton.click();
    success.focus();
    window.history.replaceState({}, '', `${window.location.pathname}${window.location.search}`);
  }
};
export default setupInquire;
